import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

/**
 * Transient outcome announcements — the toast states the canonical authority
 * shows after freeze, copy, accept and submit (#518).
 *
 * This is the product's FIRST toast mechanism, so its contract is written down
 * rather than assumed:
 *
 * - A toast is an OUTCOME STATEMENT, never a control. It carries no button, no
 *   link and no dismissal affordance, because nothing about it is actionable —
 *   the action already happened, and every failure state in this application
 *   renders as a persistent notice beside the control that failed, not as a
 *   toast that evaporates mid-read.
 * - The viewport is ALWAYS MOUNTED and is one polite live region, for the same
 *   reason `Notice` keeps its region mounted: assistive technology observes a
 *   message arriving in an existing region rather than a whole region
 *   appearing. `aria-atomic` is deliberately absent — additions are announced
 *   as additions, and re-reading the whole stack on every arrival would repeat
 *   messages that were already read.
 * - The viewport carries `aria-live` WITHOUT `role="status"`, and is portalled
 *   to `document.body`. Both halves are load-bearing for the rest of the
 *   suite as much as for users: a standing `role="status"` element would make
 *   every `getByRole("status")` query in the application ambiguous, and a
 *   viewport rendered inline would make "renders nothing" assertions false
 *   for every surface under this provider. A labelled `section` announces
 *   identically and stays out of both.
 * - Toasts auto-retire. The message is also always stated somewhere durable —
 *   a card state, a status line, the page the navigation landed on — so a
 *   missed toast loses emphasis, never information.
 */
type Toast = { id: number; message: string };

const ToastContext = createContext<((message: string) => void) | null>(null);

/** How long a toast stays. Long enough to read a short sentence twice. */
const TOAST_DURATION_MS = 5_000;

export function useToast(): (message: string) => void {
  const push = useContext(ToastContext);
  if (push === null) {
    throw new Error("useToast requires a <ToastProvider> above it.");
  }
  return push;
}

export function ToastProvider({
  children,
  duration = TOAST_DURATION_MS,
}: {
  children: ReactNode;
  /** Test seam. Production callers take the default. */
  duration?: number;
}) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(0);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const push = useCallback(
    (message: string) => {
      const id = nextId.current++;
      setToasts((current) => [...current, { id, message }]);
      const timer = setTimeout(() => {
        timers.current.delete(id);
        setToasts((current) => current.filter((toast) => toast.id !== id));
      }, duration);
      timers.current.set(id, timer);
    },
    [duration],
  );

  // The timers map is a ref this component owns for its whole life; reading it
  // in the unmount cleanup is exactly its purpose.
  useEffect(() => {
    const owned = timers.current;
    return () => {
      for (const timer of owned.values()) {
        clearTimeout(timer);
      }
      owned.clear();
    };
  }, []);

  const value = useMemo(() => push, [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/*
        z-index above the dialog layer (z-50): a toast reports the outcome of
        an action a dialog just performed, and a report the dialog covers is a
        report that was never made. `pointer-events-none` on the viewport so an
        empty region never intercepts a click aimed at what is beneath it.
      */}
      {createPortal(
        <section
          aria-live="polite"
          aria-label="Notifications"
          className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-[min(22rem,calc(100vw-2rem))] flex-col items-end gap-2"
        >
          {toasts.map((toast) => (
            <p
              key={toast.id}
              className="w-fit max-w-full rounded-md border border-border bg-card px-4 py-2.5 text-sm text-foreground shadow-lg"
            >
              {toast.message}
            </p>
          ))}
        </section>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}
